import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight } from "lucide-react";
import { colors } from "../components/landing/colors";
import { LandingFooter } from "../components/landing/LandingFooter";

const API = '/api';

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      // HPS-Partner direkt in den Partnerbereich
      if (user.role === "partner_hps") {
        navigate("/app/hps-anlagencheck", { replace: true });
        return;
      }
      const isMobile = window.innerWidth < 768;
      const isAdmin = user.role === "admin";
      const isWorker = user.role === "mitarbeiter" || user.role === "nachunternehmer";
      if (isMobile && isAdmin) {
        navigate("/app/m/dashboard-admin");
      } else if (isWorker || isMobile) {
        // /app/m statt /app/m/stempeluhr → MobileStartRedirect entscheidet (Stundenzettel-User landen direkt am Stundenzettel)
        navigate("/app/m");
      } else {
        navigate("/app");
      }
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const loggedInUser = await login(email, password);
      toast.success("Erfolgreich angemeldet");
      if (loggedInUser?.role === "partner_hps") {
        navigate("/app/hps-anlagencheck", { replace: true });
        return;
      }
      const isMobile = window.innerWidth < 768;
      const isAdmin = loggedInUser?.role === "admin";
      const isWorker = loggedInUser?.role === "mitarbeiter" || loggedInUser?.role === "nachunternehmer";
      if (isMobile && isAdmin) {
        navigate("/app/m/dashboard-admin");
      } else if (isWorker || isMobile) {
        // /app/m statt /app/m/stempeluhr → MobileStartRedirect entscheidet (Stundenzettel-User landen direkt am Stundenzettel)
        navigate("/app/m");
      } else {
        navigate("/app");
      }
    } catch (error) {
      toast.error(error.message || "Login fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col font-['Inter',system-ui,sans-serif]" style={{ backgroundColor: colors.darkBlue }}>

      {/* Header */}
      <nav style={{ backgroundColor: "#ffffff", height: "88px" }}>
        <div className="h-full max-w-7xl mx-auto flex items-center px-6 md:px-12">
          <a href="/" className="flex items-center h-full">
            <img
              src="/logo-dark.png"
              alt="TGAone"
              style={{ height: "72px", width: "auto" }}
              data-testid="login-logo"
            />
          </a>
        </div>
      </nav>

      {/* Login Content */}
      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          <div className="text-center mb-10">
            <h1 className="text-white font-bold text-2xl tracking-tight">Interner Bereich</h1>
            <p className="text-white/40 text-sm mt-2">Melden Sie sich an, um fortzufahren.</p>
          </div>

          <form onSubmit={handleSubmit} noValidate className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-[13px] font-medium text-white/60 mb-2">E-Mail</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ihre@email.de"
                required
                className="w-full h-12 px-4 bg-white border border-gray-200 text-gray-900 text-[15px] placeholder:text-gray-300 focus:outline-none focus:border-gray-900 transition-colors"
                data-testid="login-email"
              />
            </div>

            <div>
              <label className="block text-[13px] font-medium text-white/60 mb-2">Passwort</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full h-12 px-4 pr-12 bg-white border border-gray-200 text-gray-900 text-[15px] placeholder:text-gray-300 focus:outline-none focus:border-gray-900 transition-colors"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-14 font-medium text-[15px] flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
              style={{ backgroundColor: "#ffffff", color: colors.darkBlue }}
              data-testid="login-submit"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2" style={{ borderColor: colors.darkBlue }} />
              ) : (
                <>Anmelden <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
          </form>
        </div>
      </div>

      <LandingFooter />
    </div>
  );
}
